import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ComboLocationSelect from '@features/ComboLocationSelect';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { Checkbox } from '@components/Checkbox';
import { Switch } from '@components/Switch';
import Button from '@components/Button';
import { MultiSelectLabels } from '@components/MultiSelectLabels';
import { Typography } from '@components/Typography';
import { ProfileType } from '@constants/integrations';
import { LabelType } from '@constants/labels';
import { useTranslation, useLocations } from '@hooks';
import AdditionalField, { CustomFieldType } from './AdditionalField';

const PlatformWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    transform: 'translateX(-7px)',
    '& .MuiCheckbox-root': {
        padding: 4,
    },
});

const FieldWrapper = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'calc(50% - 8px) calc(50% - 8px)',
    gridGap: 16,
    marginTop: '8px',
    alignItems: 'center',
});

const StyledThreshold = styled(Select)(({ value }) => ({
    width: 'fit-content',
    '& .MuiFormControl-root': {
        paddingBottom: 0,
    },
    ...(value !== 0 && {
        minWidth: '70px',
    }),
}));

const General = ({ isView, brands, selectedSurvey, availablePlatforms }) => {
    const { data: labels } = useSelector(state => state.labels);

    const { t } = useTranslation();
    const { control, setValue, getValues, fields, append, watch } = useFormContext();
    const { getSelectLocationsOptions } = useLocations();

    const satisfactionSurveyEnabled = watch('satisfactionSurveyEnabled');
    const emailField = watch('contactEmail');
    const phoneField = watch('contactPhone');
    const nameField = watch('contactName');

    const brandsOptions = useMemo(() => brands.map(item => ({ value: item.id, label: item.title })), [brands]);

    const locationsOptions = useMemo(() => getSelectLocationsOptions(), []);

    const labelsOptions = useMemo(() => labels.map(item => ({ value: item.id, label: item.name })), [labels]);

    return (
        <>
            <Controller
                control={control}
                name="name"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('FeedbackSurveys.surveyName')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                        disabled={isView}
                        sx={{ marginBottom: '8px' }}
                    />
                )}
            />
            <ComboLocationSelect
                brandsOptions={brandsOptions}
                locationsOptions={locationsOptions}
                data={selectedSurvey}
                disabled={isView}
                isView={isView}
                updatedLocationCb={value => setValue('mainLocationId', value)}
            />
            <Controller
                control={control}
                name="satisfactionSurveyEnabled"
                render={({ field, ref }) => (
                    <Switch
                        {...field}
                        ref={ref}
                        checked={getValues('satisfactionSurveyEnabled')}
                        handleChange={event => {
                            setValue('satisfactionSurveyEnabled', event.target.checked, { shouldDirty: true });
                        }}
                        label={t('FeedbackSurveys.nps')}
                        disabled={isView}
                    />
                )}
            />
            {satisfactionSurveyEnabled && (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <Typography
                            variant="body2"
                            sx={{ color: 'rgba(0, 0, 0, 0.87)', marginTop: '8px', marginBottom: '16px' }}
                        >
                            {t('FeedbackSurveys.satisfactionSurveyThreshold')}
                        </Typography>
                        <Controller
                            control={control}
                            name="satisfactionSurveyThreshold"
                            render={({ field, ref }) => (
                                <StyledThreshold
                                    {...field}
                                    onChange={e => {
                                        setValue(`satisfactionSurveyThreshold`, e.target.value, {
                                            shouldDirty: true,
                                        });
                                    }}
                                    ref={ref}
                                    label={t('common.rating')}
                                    options={[
                                        { label: '5', value: 5 },
                                        { label: '4', value: 4 },
                                        { label: '3', value: 3 },
                                        { label: '2', value: 2 },
                                        { label: t('common.disabled'), value: 1 },
                                    ]}
                                    disabled={isView}
                                />
                            )}
                        />
                    </div>
                    <Typography
                        variant="subtitle1"
                        sx={{ color: 'rgba(0, 0, 0, 0.87)', marginTop: '8px', marginBottom: '16px' }}
                    >
                        {t('FeedbackSurveys.contactDetails')}
                    </Typography>
                    <FieldWrapper>
                        <Controller
                            control={control}
                            name="contactEmail"
                            render={({ field, ref }) => (
                                <Switch
                                    {...field}
                                    ref={ref}
                                    checked={getValues('contactEmail')}
                                    handleChange={event => {
                                        setValue('contactEmail', event.target.checked, { shouldDirty: true });
                                        if (!event.target.checked) {
                                            setValue('contactEmailRequired', false, { shouldDirty: true });
                                        }
                                    }}
                                    label={t('FeedbackSurveys.contactEmail')}
                                    disabled={isView}
                                />
                            )}
                        />
                        {emailField ? (
                            <PlatformWrapper>
                                <Controller
                                    control={control}
                                    name="contactEmailRequired"
                                    render={({ field, ref }) => (
                                        <Checkbox
                                            ref={ref}
                                            disabled={isView}
                                            inputProps={{ 'aria-label': 'controlled' }}
                                            onChange={e => {
                                                setValue('contactEmailRequired', e.target.checked, {
                                                    shouldDirty: true,
                                                });
                                            }}
                                            checked={field.value}
                                        />
                                    )}
                                />
                                <Typography variant="body2">{t('common.required')}</Typography>
                            </PlatformWrapper>
                        ) : (
                            <div />
                        )}
                    </FieldWrapper>
                    <FieldWrapper>
                        <Controller
                            control={control}
                            name="contactPhone"
                            render={({ field, ref }) => (
                                <Switch
                                    {...field}
                                    ref={ref}
                                    checked={getValues('contactPhone')}
                                    handleChange={event => {
                                        setValue('contactPhone', event.target.checked, { shouldDirty: true });
                                        if (!event.target.checked) {
                                            setValue('contactPhoneRequired', false, { shouldDirty: true });
                                        }
                                    }}
                                    label={t('FeedbackSurveys.contactPhone')}
                                    disabled={isView}
                                />
                            )}
                        />
                        {phoneField ? (
                            <PlatformWrapper>
                                <Controller
                                    control={control}
                                    name="contactPhoneRequired"
                                    render={({ field, ref }) => (
                                        <Checkbox
                                            ref={ref}
                                            disabled={isView}
                                            inputProps={{ 'aria-label': 'controlled' }}
                                            onChange={e => {
                                                setValue('contactPhoneRequired', e.target.checked, {
                                                    shouldDirty: true,
                                                });
                                            }}
                                            checked={field.value}
                                        />
                                    )}
                                />
                                <Typography variant="body2">{t('common.required')}</Typography>
                            </PlatformWrapper>
                        ) : (
                            <div />
                        )}
                    </FieldWrapper>
                    <FieldWrapper>
                        <Controller
                            control={control}
                            name="contactName"
                            render={({ field, ref }) => (
                                <Switch
                                    {...field}
                                    ref={ref}
                                    checked={getValues('contactName')}
                                    handleChange={event => {
                                        setValue('contactName', event.target.checked, { shouldDirty: true });
                                        if (!event.target.checked) {
                                            setValue('contactNameRequired', false, { shouldDirty: true });
                                        }
                                    }}
                                    label={t('FeedbackSurveys.contactName')}
                                    disabled={isView}
                                />
                            )}
                        />
                        {nameField ? (
                            <PlatformWrapper>
                                <Controller
                                    control={control}
                                    name="contactNameRequired"
                                    render={({ field, ref }) => (
                                        <Checkbox
                                            ref={ref}
                                            disabled={isView}
                                            inputProps={{ 'aria-label': 'controlled' }}
                                            onChange={e => {
                                                setValue('contactNameRequired', e.target.checked, {
                                                    shouldDirty: true,
                                                });
                                            }}
                                            checked={field.value}
                                        />
                                    )}
                                />
                                <Typography variant="body2">{t('common.required')}</Typography>
                            </PlatformWrapper>
                        ) : (
                            <div />
                        )}
                    </FieldWrapper>
                    <div>
                        {fields.map((field, index) => (
                            <AdditionalField key={field.id} index={index} isView={isView} />
                        ))}
                    </div>
                    <Button
                        variant="outlined"
                        sx={{ width: 'max-content', margin: '16px 0 24px' }}
                        startIcon={<AddIcon />}
                        disabled={isView}
                        onClick={() => {
                            append({ name: '', type: CustomFieldType.TEXT, required: false });
                        }}
                    >
                        {t('FeedbackSurveys.addField')}
                    </Button>
                    <Controller
                        control={control}
                        name="labels"
                        render={({ field, ref }) => (
                            <MultiSelectLabels
                                {...field}
                                ref={ref}
                                onChange={value => {
                                    setValue('labels', value, { shouldDirty: true });
                                }}
                                options={labelsOptions}
                                label={t('common.labels')}
                                fullWidth
                                disabled={isView}
                                type={LabelType.Contact}
                                onCreateCallback={newLabel => {
                                    const values = getValues();
                                    setValue('labels', [...values.labels, newLabel], {
                                        shouldDirty: true,
                                    });
                                }}
                            />
                        )}
                    />
                </>
            )}
            <div style={{ paddingTop: 8 }}>
                <Typography variant="subtitle1">{t('FeedbackSurveys.reviewSites')}</Typography>
                <div style={{ paddingTop: 16 }}>
                    {availablePlatforms.map((platform, index) => (
                        <PlatformWrapper key={index}>
                            <Controller
                                control={control}
                                name="reviewPlatforms"
                                render={({ field, ref }) => (
                                    <Checkbox
                                        ref={ref}
                                        disabled={isView}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                        onChange={e => {
                                            const platforms = !e.target.checked
                                                ? field.value.filter(val => val !== platform.type)
                                                : [...field.value, platform.type];
                                            setValue('reviewPlatforms', platforms, { shouldDirty: true });
                                        }}
                                        checked={field.value.includes(platform.type)}
                                    />
                                )}
                            />
                            <Typography variant="body2">{`${ProfileType[platform.type]}${
                                platform.isInherited ? ` (${t('SettingsBrands.mainLocation')})` : ''
                            }`}</Typography>
                        </PlatformWrapper>
                    ))}
                </div>
            </div>
        </>
    );
};

export default General;
